/**
 * The words the attention components show that are not an item's own
 * (twin slice 1, plan W5 step 13).
 *
 * The cards, the column and the verdict bar render text from the selector
 * and the copy tables only. What is left -- group headings, the "+N more"
 * and "Details" controls, the two card actions, and the one line that
 * says where and since when -- lives here, in one table, so a copy pass
 * reads it in one place and no component writes a hotel word of its own.
 *
 * COPY STATUS: pending Akash's copy pass 1 (plan gate B); listed in the W5
 * wave-B report as COPY FOR REVIEW.
 */
import type { AttentionItem } from './select';
import { dayClockText, levelWords, type SiteTimeZone } from './words';

export const ATTENTION_LABELS = {
  /** The column's accessible name. */
  column: 'What needs attention',
  needsYou: 'Needs you',
  nexsoss: 'Nexsoss',
  /** Shown, nobody asked yet; and what has cleared. */
  info: 'Info',
  pickedUp: (n: number) => `Picked up (${n})`,
  more: (n: number) => `+${n} more`,
  fewer: 'Show fewer',
  details: 'Details',
  also: 'Also:',
  showWhere: 'Show where',
  openPanel: (name: string) => `Open ${name} panel`,
  done: 'Done',
  rightNow: 'Right now',
  simulated: 'SIM',
  /** The band glyph's accessible name. */
  band: { safety: 'Safety', blocked: 'Blocked', upkeep: 'Upkeep' },
} as const;

/** How many Needs-you cards show before "+N more" (ideation §6). */
export const NEEDS_YOU_SHOWN = 3;

/**
 * One line under a card's headline: the floor, and when it started.
 *
 * The floor is a robot's, or a door's (a door alert's own card, which has
 * no other line to say it in); a car's W6 lines and a site row say their
 * own.
 *
 * "since HH:MM" only from a stamp of the item's own -- the alert that
 * backs it (design principle 3), or a car's own start (W6's). A car card
 * already says its start in W6's Robots line ("Kept out since 14:33"), so
 * it is not said twice; a SITE row that absorbed the car shows no car
 * lines, so the row says it here -- or it would be said nowhere. Without
 * a stamp, "seen here since HH:MM": when THIS page first saw it. A card
 * that looks fixed or has cleared says that instead (its `statusLine`),
 * with no start.
 *
 * Every time names its day when it is not today ("yesterday 23:58",
 * `dayClockText`), as the Overview heading and the verdict do: a card
 * left up overnight never reads as this morning's.
 */
export function cardMetaLine(item: AttentionItem, clock: SiteTimeZone): string | null {
  const parts: string[] = [];
  const level = levelWords(item.level);
  const when = (ms: number) => dayClockText(ms, clock, item.wallNowMs);
  if (level && (item.subject.kind === 'robot' || item.subject.kind === 'door')) parts.push(level);
  if (item.status !== 'active') {
    if (item.statusLine) parts.push(item.statusLine);
  } else if (item.sinceMs !== null && item.sinceSource === 'alert') {
    parts.push(`since ${when(item.sinceMs)}`);
  } else if (item.sinceMs !== null && item.sinceSource === 'lift' && item.subject.kind !== 'lift') {
    parts.push(`since ${when(item.sinceMs)}`);
  } else if (item.sinceSource !== 'lift' && item.seenSinceMs !== null) {
    parts.push(`seen here since ${when(item.seenSinceMs)}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}
